import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HttpParamsHelpersService } from '@zaoblako/web/core/services/http-params-helpers.service';
import { JsonataService } from '@zaoblako/web/core/services/jsonata.service';
import { HttpMethodEnum } from '@zaoblako/shared/modules/security/enums/http.method.enum';
import type { StandardDropdownDescriptorInterface } from '@zaoblako/shared/common/dtos/standard-dropdown/standard-dropdown-descriptor.dto';
import type { StandardDropdownItemInterface } from '@zaoblako/shared/common/interfaces/standard-dropdown-item.interface';

import { isNil } from 'lodash';
import { of, throwError, type Observable } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';

export type StandardDropdownPageErrorKind = 'request' | 'transform' | 'descriptor';

/** Items request failed, the projection failed, or the descriptor cannot load a page. */
export class StandardDropdownPageError extends Error {
  public readonly kind: StandardDropdownPageErrorKind;

  public constructor(kind: StandardDropdownPageErrorKind) {
    super(kind);
    this.kind = kind;
  }
}

/** One page of dropdown entities plus their display projection */
export interface StandardDropdownPageResult<T> {
  limit: number;
  offset: number;
  count: number;
  items: T[];
  transformed: Record<string | number, StandardDropdownItemInterface>;
}

/** Provides utils for standard dropdowns */
@Injectable({ providedIn: 'root' })
export class StandardDropdownsService {
  private readonly httpClient = inject(HttpClient);
  private readonly httpParamsHelpersService = inject(HttpParamsHelpersService);
  private readonly jsonataService = inject(JsonataService);

  private readonly descriptorsCache = new Map<string, Observable<StandardDropdownDescriptorInterface>>();

  /**
   * Get dropdown descriptor by URL. Descriptors will be cached in RAM by default
   *
   * @param url Descriptor URL (must be valid HTTP GET endpoint)
   * @param [noCache] If `true` then descriptor will not be cached in RAM
   *
   * @returns `Observable` with dropdown descriptor
   */
  public getDescriptor(url: string, noCache?: boolean): Observable<StandardDropdownDescriptorInterface> {
    const sanitizedUrl = this.sanitizeUrl(url);
    if (noCache) {
      return this.httpClient.get<StandardDropdownDescriptorInterface>(sanitizedUrl);
    }

    const cached = this.descriptorsCache.get(sanitizedUrl);
    if (cached) {
      return cached;
    }

    const result = this.httpClient
      .get<StandardDropdownDescriptorInterface>(sanitizedUrl)
      .pipe(
        tap({
          error: () => this.descriptorsCache.delete(sanitizedUrl)
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    this.descriptorsCache.set(sanitizedUrl, result);

    return result;
  }

  /**
   * Load one page of dropdown items and project them into the standard item shape.
   *
   * @param descriptor Dropdown descriptor
   * @param [params] Query or body parameters. Same keys override `descriptor.predefinedFilters`.
   *
   * @returns Page with raw items and display projection by key
   */
  public loadPage = <T extends Record<string, any>>(
    descriptor: StandardDropdownDescriptorInterface,
    params?: Record<string, any>
  ): Observable<StandardDropdownPageResult<T>> => {
    const requestParams = isNil(descriptor?.predefinedFilters)
      ? params
      : { ...descriptor.predefinedFilters, ...(params ?? {}) };
    const request = this.createItemsRequest(descriptor, requestParams);
    if (!request) {
      return throwError(() => new StandardDropdownPageError('descriptor'));
    }

    return request.pipe(
      catchError(() => throwError(() => new StandardDropdownPageError('request'))),
      switchMap((response) => this.toPage<T>(descriptor, response))
    );
  };

  /**
   * Reset descriptors cache
   *
   * @param [url] If defined then only cached descriptor for this URL will be deleted
   */
  public clearDescriptorsCache(url?: string): void {
    if (url) {
      const sanitizedUrl = this.sanitizeUrl(url);
      this.descriptorsCache.delete(sanitizedUrl);
    } else {
      this.descriptorsCache.clear();
    }
  }

  private createItemsRequest = (
    descriptor: StandardDropdownDescriptorInterface,
    params?: Record<string, any>
  ): Observable<any> | null => {
    if (!descriptor?.api?.url || !descriptor.api.method) {
      return null;
    }

    const url = this.sanitizeUrl(descriptor.api.url);
    const httpParams = params ? this.httpParamsHelpersService.objectToHttpParams(params) : {};

    switch (descriptor.api.method) {
      case HttpMethodEnum.POST:
        return this.httpClient.post(url, params);
      case HttpMethodEnum.PUT:
        return this.httpClient.put(url, params);
      case HttpMethodEnum.PATCH:
        return this.httpClient.patch(url, params);
      case HttpMethodEnum.DELETE:
        return this.httpClient.delete(url, { params: httpParams });
      case HttpMethodEnum.GET:
        return this.httpClient.get(url, { params: httpParams });
      default:
        return null;
    }
  };

  private toPage = <T extends Record<string, any>>(
    descriptor: StandardDropdownDescriptorInterface,
    response: any
  ): Observable<StandardDropdownPageResult<T>> => {
    const page = descriptor?.page;
    const keyProperty = descriptor?.properties?.key;
    if (!page || !keyProperty || !descriptor.transform) {
      return throwError(() => new StandardDropdownPageError('descriptor'));
    }

    const items = (Array.isArray(response?.[page.items]) ? response[page.items] : []) as T[];
    const count = Number(response?.[page.count] ?? items.length);
    const limit = Number(response?.[page.limit] ?? items.length);
    const offset = Number(response?.[page.offset] ?? 0);
    const pageResult = (transformed: Record<string | number, StandardDropdownItemInterface>): StandardDropdownPageResult<T> => ({
      limit,
      offset,
      count,
      items,
      transformed
    });

    if (!items.length) {
      return of(pageResult({}));
    }

    return this.jsonataService
      .transformAsObservable<T[], StandardDropdownItemInterface[]>(items, descriptor.transform)
      .pipe(
        map((transformedList) => {
          const transformed: Record<string | number, StandardDropdownItemInterface> = {};
          for (const [index, item] of items.entries()) {
            const key = item?.[keyProperty] as string | number;
            const projected = transformedList?.[index];
            if (isNil(key) || isNil(projected)) {
              continue;
            }
            transformed[key] = projected;
          }
          return pageResult(transformed);
        }),
        catchError(() => throwError(() => new StandardDropdownPageError('transform')))
      );
  };

  /**
   * Remove API prefix from URL
   *
   * @param url URL to sanitize
   *
   * @returns Sanitized URL
   */
  private sanitizeUrl(url: string): string {
    let cleanUrl = url.startsWith('/') ? url.slice(1) : url;

    if (cleanUrl.startsWith('api/v1/')) {
      cleanUrl = cleanUrl.slice(7);
    }

    return `/${cleanUrl}`;
  }
}
